import './styles.css';
import { initSentry } from './lib/sentry';
import { supabase } from './lib/supabase';
import { toast } from './lib/ui';

initSentry();
import { checkAccess } from './services/access';
import { invalidateTargets, invalidateWriteMemo } from './services/entries';
import { invalidateMealPlan } from './services/mealPlan';
import { photoQueue, jobKind } from './services/photoQueue';
import { renderAuth, attachAuth, renderPending, renderBootError, attachPending,
         renderRecovery, attachRecovery } from './screens/auth';
import { renderSettings, attachSettings } from './screens/settings';
import { renderHome, attachHome, homeAddDate } from './screens/home';
import { renderProgress, attachProgress } from './screens/progress';
import { renderCoach, attachCoach } from './screens/coach';
import { renderViewer, attachViewer } from './screens/viewer';
import { renderAdmin, attachAdmin } from './screens/admin';
import { BACK_ARROW_SVG } from './screens/addSheet';
import { openAddSheet, closeAddFlowExternal, invalidateAddFlowCache } from './screens/addSheet';

type TabName = 'home' | 'progress' | 'coach' | 'settings';

const screenEl = () => document.getElementById('screen')!;

interface Screen {
  render: () => string;
  attach?: (root: HTMLElement) => void;
}

// מסכי placeholder מוחלפים במימוש אמיתי, מסך-מסך
const screens: Record<TabName, Screen> = {
  home: { render: renderHome, attach: attachHome },
  progress: { render: renderProgress, attach: attachProgress },
  coach: { render: renderCoach, attach: attachCoach },
  settings: {
    render: renderSettings,
    attach: (root) => attachSettings(root, {
      openViewer: () => openSubScreen(renderViewer, attachViewer),
      openAdmin: () => openSubScreen(renderAdmin, attachAdmin),
    }),
  },
};

let activeTab: TabName = 'home';

function switchTab(tab: TabName): void {
  activeTab = tab;
  const screen = screens[tab];
  screenEl().innerHTML = screen.render();
  screen.attach?.(screenEl());
  document.querySelectorAll<HTMLButtonElement>('.nav-item').forEach((btn) => {
    btn.classList.toggle('active', btn.dataset.tab === tab);
  });
  window.scrollTo(0, 0);
}

/** מסך משנה (ליווי/ניהול) — נפתח מתוך ההגדרות עם כפתור חזרה */
function openSubScreen(render: () => string, attach: (root: HTMLElement) => void): void {
  // חץ SVG שפונה החוצה (ימינה ב-RTL) — התו ‹ מתהפך ע"י ה-bidi
  screenEl().innerHTML = `<button class="subscreen-back" id="sub-back">${BACK_ARROW_SVG} הגדרות</button>${render()}`;
  document.getElementById('sub-back')!.addEventListener('click', () => switchTab('settings'));
  attach(screenEl());
  window.scrollTo(0, 0);
}

function setInApp(inApp: boolean): void {
  document.body.classList.toggle('in-app', inApp);
}

function showAuth(notice?: string): void {
  setInApp(false);
  screenEl().innerHTML = renderAuth();
  attachAuth(() => void enterApp(), notice);
}

// נחיתה מקישור מייל. נקרא מוקדם, כי supabase-js מנקה את ה-hash אחרי שהוא צורך
// את הטוקן — ואז אי אפשר יהיה לדעת מה הייתה סיבת הנחיתה.
const landingHash = window.location.hash;

// דגל שורד-רענון: בלי זה, משתמשת שנוחתת מקישור שחזור ומרעננת לפני שקבעה סיסמה
// פשוט נכנסת לאפליקציה — עם הסיסמה הישנה שאותה לא זכרה מלכתחילה.
// ב-localStorage ולא ב-sessionStorage: הסשן עצמו ב-localStorage ומשותף לכל הטאבים,
// ולכן דגל per-tab היה נעקף פשוט ע"י פתיחת טאב שני.
const RECOVERY_FLAG = 'lbRecoveryPending';
const setRecoveryPending = (on: boolean) =>
  on ? localStorage.setItem(RECOVERY_FLAG, '1') : localStorage.removeItem(RECOVERY_FLAG);
if (/[#&]type=recovery/.test(landingHash)) setRecoveryPending(true);
const isRecoveryPending = () => localStorage.getItem(RECOVERY_FLAG) === '1';

// קישור שפג תוקף חוזר עם #error=... ובלי type=recovery, ולכן לא נכנס למסלול השחזור.
// בלי הקריאה הזו המשתמשת רואה מסך כניסה רגיל ולא מבינה למה.
function landingError(): string | null {
  const p = new URLSearchParams(landingHash.replace(/^#/, ''));
  if (!p.get('error') && !p.get('error_code')) return null;
  // GoTrue לא עקבי: לפעמים error_code=otp_expired, ולפעמים רק error=access_denied
  // עם ההסבר בתוך error_description. בלי לבדוק את שניהם, קישור שפג תוקף מקבל
  // את ההודעה השגויה "כבר נוצל".
  const hay = `${p.get('error_code') ?? ''} ${p.get('error_description') ?? ''}`;
  return /expired/i.test(hay) ? 'הקישור פג תוקף — בקשי קישור חדש'
    : /access_denied/i.test(p.get('error') ?? '') ? 'הקישור כבר נוצל או בוטל — בקשי קישור חדש'
    : 'הקישור לא תקין — בקשי קישור חדש';
}

function showRecovery(): void {
  setInApp(false);
  screenEl().innerHTML = renderRecovery();
  attachRecovery(
    () => { setRecoveryPending(false); void enterApp(); },
    // מוצא: מי שנחתה מקישור והתחרטה הייתה תקועה עד סגירת הטאב.
    () => { setRecoveryPending(false); void supabase.auth.signOut(); },
  );
}

function showApp(): void {
  setInApp(true);
  switchTab(activeTab);
}

// כניסה לאפליקציה אחרי שיש סשן: בודקים אישור גישה.
// 'error' (רשת/שרת) לעולם לא מוצג כ"לא מאושר" — רק מסך ניסיון חוזר.
async function enterApp(): Promise<void> {
  const access = await checkAccess();
  if (access === 'approved') {
    // צילומים שחיכו לניתוח (גם מלפני סגירת האפליקציה) — ממשיכים לנסות מיד
    const { data: { session } } = await supabase.auth.getSession();
    if (session) void photoQueue.start(session.user.id);
    showApp();
    return;
  }
  setInApp(false);
  screenEl().innerHTML = access === 'pending' ? renderPending() : renderBootError();
  attachPending(() => void enterApp());
}

// מעקב מקלדת גלובלי: מפרסם את גובה המקלדת ב-CSS var --kb ומסמן body.kb-open,
// כדי שרכיבים דבוקים-לתחתית (קלט המאמנת, שדות בתחתית ההגדרות) יעלו מעל המקלדת.
function trackKeyboard(): void {
  const vv = window.visualViewport;
  if (!vv) return;
  let raf = 0;
  let kbOpen = false;
  const apply = () => {
    raf = 0;
    const kb = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
    document.documentElement.style.setProperty('--kb', `${kb}px`);
    // היסטרזיס: פותחים רק מעל 120px, סוגרים רק מתחת ל-60px — מונע ריצוד/קפיצה
    // של ה-class בזמן אנימציית פתיחת/סגירת המקלדת (visualViewport יורה עשרות אירועים).
    const open = kbOpen ? kb > 60 : kb > 120;
    if (open !== kbOpen) {
      kbOpen = open;
      document.body.classList.toggle('kb-open', open);
    }
  };
  // rAF-throttle: מרכזים את כל האירועים לעדכון אחד לפריים — בלי layout thrash.
  const schedule = () => { if (!raf) raf = requestAnimationFrame(apply); };
  vv.addEventListener('resize', schedule);
  vv.addEventListener('scroll', schedule);
  schedule();
}

/** מקלדת נקייה בכל האפליקציה: בלי תיקון אוטומטי ובלי שורת ההצעות (QuickType) בשדות טקסט.
 *  Observer שמחיל את התכונות על כל שדה טקסט/חיפוש/אימייל/textarea שנוסף ל-DOM.
 *  חוץ מ-`data-kb="text"` — טקסט חופשי ל-AI (תיאור ארוחה, רמז לתמונה, תיקון, מאמנת): שם התיקון
 *  האוטומטי של האייפון עוזר, ובלעדיו שגיאות הקלדה הגיעו כמו שהן למודל (בר, 04.10.2026). */
function cleanKeyboardOnce(): void {
  const SEL = 'input[type="text"]:not([data-kb="text"]), input[type="search"], input[type="email"], textarea:not([data-kb="text"])';
  const apply = (el: Element) => {
    el.setAttribute('autocorrect', 'off');
    el.setAttribute('autocapitalize', 'off');
    el.setAttribute('spellcheck', 'false');
  };
  const scan = (node: Element) => {
    if (node.matches?.(SEL)) apply(node);
    node.querySelectorAll?.(SEL).forEach(apply);
  };
  new MutationObserver((muts) => {
    for (const m of muts) for (const n of m.addedNodes) {
      if (n.nodeType === 1) scan(n as Element);
    }
  }).observe(document.body, { childList: true, subtree: true });
  scan(document.body);
}

/** תוצאת ניתוח צילום שאף מסך לא חיכה לה (היא סגרה והמשיכה) — הודעה, והכרטיס בבית מתעדכן. */
function bindPhotoQueueResults(): void {
  const hhmm = (iso: string) => new Intl.DateTimeFormat('he-IL', { hour: '2-digit', minute: '2-digit' }).format(new Date(iso));
  photoQueue.onResult((job) => {
    if (!document.body.classList.contains('in-app')) return;
    const when = hhmm(job.eatenAtIso);
    const k = jobKind(job);
    const what = k === 'text' ? 'התיאור' : k === 'voice' ? 'ההקלטה' : 'הצילום';
    const done = k === 'voice' ? 'נותחה' : 'נותח';
    if (job.status === 'ready') toast(`${what} מ-${when} ${done} — מחכה לאישורך במסך הבית`);
    else if (job.status === 'clarify') toast(`יש שאלה על ${what} מ-${when} — במסך הבית`);
    else toast(`${what} מ-${when} לא ${done} — אפשר לנסות שוב ממסך הבית`, 'error');
  });
}

async function boot(): Promise<void> {
  bindPhotoQueueResults();
  trackKeyboard();
  cleanKeyboardOnce();
  document.querySelectorAll<HTMLButtonElement>('.nav-item').forEach((btn) => {
    btn.addEventListener('click', () => switchTab(btn.dataset.tab as TabName));
  });
  document.getElementById('fab')!.addEventListener('click', () => {
    if (!document.body.classList.contains('in-app')) return;
    // הוספה מתועדת ליום המוצג בבית (אם הבית פעיל), אחרת היום
    const day = activeTab === 'home' ? homeAddDate() : new Date();
    openAddSheet(() => switchTab(activeTab), day);
  });

  supabase.auth.onAuthStateChange((event) => {
    if (event === 'SIGNED_OUT') {
      // בלי הניקוי כאן, דגל שנשאר דלוק אחרי נפילת סשן היה מחזיר את מסך
      // השחזור בכל רענון — לולאה שאי אפשר לצאת ממנה.
      setRecoveryPending(false);
      // קאשים ברמת מודול שורדים יציאה והתחברות מחדש באותה לשונית (אין reload
      // בקוד) — בלי האיפוס, המשתמש הבא היה רואה את היעדים ואת התפריט של הקודם.
      invalidateTargets();
      invalidateMealPlan();
      invalidateWriteMemo();
      closeAddFlowExternal();   // אחרת מסך ההוספה נשאר פרוס מעל מסך ההתחברות
      invalidateAddFlowCache(); // תדירים / אחרונות / חיפוש של המשתמשת הקודמת
      photoQueue.stop();        // הצילומים שלה נשארים באחסון — לא בזיכרון, ולא מול המשתמש הבא
      showAuth();
    } else if (event === 'PASSWORD_RECOVERY') showRecovery();
  });

  const err = landingError();
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) {
    setRecoveryPending(false);
    showAuth(err ?? undefined);
    return;
  }
  // קישור פגום שנלחץ בזמן שיש סשן חי: בלי זה ההודעה נבלעה לגמרי.
  if (err) toast(err);
  // קישור שחזור יוצר סשן תקין — בלי הבדיקה הזו היינו נכנסים לאפליקציה
  // במקום לתת לקבוע סיסמה חדשה. הדגל שורד רענון עד שנקבעה סיסמה בפועל.
  if (isRecoveryPending()) {
    showRecovery();
    return;
  }
  await enterApp();
}

void boot();
